import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { PAYMENT_METHOD_ECOMMERCE_GTM } from '@airasia-common/libraries/constants/Analytics';
import { DECIMAL_TYPE } from './constants';

const roundToTwo = num => {
  return +`${Math.round(`${num}e+2`)}e-2`;
};

export const currencyFormat = (amount, decimalType, onlyPrice = false) => {
  const priceInNumber = amount * 1; // conver string to number
  let price = priceInNumber;
  switch (decimalType) {
    case DECIMAL_TYPE.FLOAT:
      price = priceInNumber.toFixed(2);
      break;
    case DECIMAL_TYPE.ROUND:
      price = Math.round(priceInNumber);
      break;
    case DECIMAL_TYPE.FLOOR:
      price = Math.floor(priceInNumber);
      break;
    case DECIMAL_TYPE.CEIL:
      price = Math.ceil(priceInNumber);
      break;
    default:
      price = Number(amount);
      price = roundToTwo(price);
  }
  return onlyPrice
    ? price
    : price && price.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const toSnakeCase = str => {
  if (!str || typeof str !== 'string' || str.length === 0) {
    return '';
  }

  const regexPattern = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;
  const matches = str.match(regexPattern);

  if (!matches) {
    return str.toLowerCase();
  }

  return matches.map(x => x.toLowerCase()).join('-');
};

export const getConfirmationPageBookingEventWebView = ({
  hotelContent,
  totalRooms,
  totalNights,
  checkin,
  checkout,
  payment,
  orderNumber,
  totalOccupancy,
  paymentDetails,
}) => {
  const { id, name, address } = hotelContent || {};
  const { city, country } = address || {};
  const { totalInclusivePrice, hotelChargedPropertyFee } = payment || {};
  const { adults, children = 0 } = totalOccupancy || {};

  let paymentmethod = '';
  if (paymentDetails && paymentDetails.length > 0) {
    for (let i = 0; i < paymentDetails.length; i++) {
      const paymentItem = paymentDetails[i];
      if (PAYMENT_METHOD_ECOMMERCE_GTM[paymentItem.payment_method]) {
        paymentmethod +=
          PAYMENT_METHOD_ECOMMERCE_GTM[paymentItem.payment_method];
        if (i < paymentDetails.length - 1) {
          paymentmethod += '|';
        }
      }
    }
  } else {
    paymentmethod = 'VI';
  }

  const bookingSuccessFulEvent = {
    type: 'firebase',
    command: 'logEvent',
    name: 'aa_htl_booking_successful',
    parameters: {
      transaction_id: orderNumber,
      currency: totalInclusivePrice?.currency,
      coupon: '',
      checkindate: checkin,
      checkoutdate: checkout,
      destination: city,
      destinationcountry: country,
      destinationcategory: 'city',
      lengthofstay: totalNights.toString(),
      rooms: totalRooms.toString(),
      totalpax: adults + children,
      adult: adults.toString(),
      child: children.toString(),
      infant: '',
      screenName: 'Booking Confirmation',
      lineofbusiness: 'Hotels SA',
      hotelid: id,
      hotelname: name,
      revenue: Number(totalInclusivePrice?.value),
    },
  };

  const purchaseEvent = {
    type: 'firebase',
    command: 'logEvent',
    name: 'purchase',
    parameters: {
      value: Number(totalInclusivePrice?.value),
      transaction_id: orderNumber,
      currency: totalInclusivePrice?.currency,
      paymentmethod,
      coupon: '',
      checkindate: checkin,
      checkoutdate: checkout,
      destination: city,
      country,
      lengthofstay: totalNights.toString(),
      rooms: totalRooms.toString(),
      totalpax: adults + children,
      adult: adults.toString(),
      kid: children.toString(),
      infant: '',
      screenName: 'Booking Confirmation',
      lineofbusiness: 'Hotels SA',
      items: [
        {
          item_id: id,
          item_name: name,
          item_category: 'Hotel SA-Hotels',
          item_brand: 'Hotel SA',
          item_variant: 'Hotel',
          items_quantity: (totalNights * totalRooms).toString(),
          price: hotelChargedPropertyFee
            ? hotelChargedPropertyFee?.amount?.value
            : 0,
          item_list_name: 'Hotel Search Results - Expedia Recommendation',
        },
      ],
    },
  };

  return {
    bookingSuccessFulEvent,
    purchaseEvent,
  };
};

export const processGalleryV2 = galleryV2 => {
  const images = [];
  if (galleryV2 && galleryV2.length > 0) {
    const primaryImage = galleryV2.find(
      item => item.category && item.category.name === 'Primary image'
    );
    if (primaryImage) {
      images.push(primaryImage.links.small);
    }
    for (let i = 0; i < Math.min(4, galleryV2.length); i++) {
      images.push(galleryV2[i].links.small);
    }
  }
  return images;
};
export const getTranslationKeyBasedonRoomandNoight = (room, night) => {
  let key = 'Rm9PriceBreakdown';
  if (room > 1 && night > 1) {
    key += '.summaryNightsKeyRooms';
  } else if (room > 1 && night === 1) {
    key += '.summaryNightKeyRooms';
  } else if (room === 1 && night > 1) {
    key += '.summaryNightsKey';
  } else {
    key += '.summaryNightKey';
  }
  return key;
};

export const getTranslatedPricebreakdownData = (
  rm9PriceBreakdownData,
  translate,
  locale,
  isRm9SpecialCurrency
) => {
  if (rm9PriceBreakdownData && Object.keys(rm9PriceBreakdownData).length) {
    const data = {
      savingLabel: `${translate('Rm9PriceBreakdown.savingLabelWeb')} `,
      firstNightKey: `${translate(
        getTranslationKeyBasedonRoomandNoight(
          Number(rm9PriceBreakdownData?.rooms),
          Number(rm9PriceBreakdownData?.night) - 1
        ),
        {
          room: rm9PriceBreakdownData?.rooms,
          night: Number(rm9PriceBreakdownData?.night) - 1,
        }
      )}`,
      secondNightKey: `${
        Number(rm9PriceBreakdownData?.rooms) < 2
          ? `${translate('Rm9PriceBreakdown.secondNightKey', {
              room: Number(rm9PriceBreakdownData?.rooms),
            })}`
          : `${translate('Rm9PriceBreakdown.secondNightKeyRooms', {
              room: Number(rm9PriceBreakdownData?.rooms),
            })}`
      }`,
      TotalSavingsKey: `${translate('Rm9PriceBreakdown.TotalSavingsKey')}`,
      perRoomNight: translate('Rm9PriceBreakdown.perRoomNight'),
      firstNightValue: localeCurrencyFormat(
        rm9PriceBreakdownData?.exclusive_display_price?.currency,
        rm9PriceBreakdownData?.exclusive_display_price?.value,
        'code',
        locale,
        true,
        null,
        true,
        false
      ),
      secondNightValue: localeCurrencyFormat(
        rm9PriceBreakdownData?.display_price?.currency,
        rm9PriceBreakdownData?.display_price?.value,
        'code',
        locale,
        true,
        null,
        true,
        false
      ),
      TotalSavingsValue: rm9PriceBreakdownData?.other_saving_display_price
        ?.value
        ? localeCurrencyFormat(
            rm9PriceBreakdownData?.other_saving_display_price?.currency,
            rm9PriceBreakdownData?.other_saving_display_price?.value,
            'code',
            locale,
            true,
            null,
            true,
            false
          )
        : 0,
      totalPayableText: `${translate('Rm9PriceBreakdown.totalPayableText')}`,
      totalAmount: localeCurrencyFormat(
        rm9PriceBreakdownData?.total_display_price?.currency,
        rm9PriceBreakdownData?.total_display_price?.value,
        'code',
        locale,
        true,
        null,
        true,
        false
      ),
      summaryNightKey: `${translate(
        getTranslationKeyBasedonRoomandNoight(
          Number(rm9PriceBreakdownData?.rooms),
          Number(rm9PriceBreakdownData?.night)
        ),
        {
          room: rm9PriceBreakdownData?.rooms,
          night: Number(rm9PriceBreakdownData?.night),
        }
      )}`,
      notificationMessage: `${translate(
        'Rm9PriceBreakdown.notificationMessage',
        {
          currency:
            rm9PriceBreakdownData?.tag?.currency +
            rm9PriceBreakdownData?.tag?.value,
        }
      )}`,
      currency: rm9PriceBreakdownData?.total_display_price?.currency,
      strikethroughKey: localeCurrencyFormat(
        rm9PriceBreakdownData?.strikethrough?.currency,
        rm9PriceBreakdownData?.strikethrough?.value,
        'code',
        locale,
        true,
        null,
        false,
        false
      ),
      RM9Val: isRm9SpecialCurrency
        ? `${rm9PriceBreakdownData?.savings?.currency}${rm9PriceBreakdownData?.savings?.value}`
        : `${rm9PriceBreakdownData?.savings?.currency}${localeCurrencyFormat(
            rm9PriceBreakdownData?.savings?.currency === 'RM'
              ? 'MYR'
              : rm9PriceBreakdownData?.savings?.currency,
            rm9PriceBreakdownData?.savings?.value,
            'code',
            locale,
            true,
            null,
            true,
            false
          )}`,
    };
    if (rm9PriceBreakdownData?.room_type) {
      data.roomType = rm9PriceBreakdownData?.room_type;
    }
    return data;
  }
  return {};
};

export const localeCurrencyFormat = (
  currencyCode,
  amount,
  display,
  locale,
  noDecimalValues = false,
  decimalType,
  hideCurrency = false,
  defaultIDRLocale
) => {
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    let formatter = '';
    let selectedLocale = '';
    if (!hideCurrency) {
      selectedLocale =
        defaultIDRLocale && currencyCode === 'IDR' ? 'id-id' : locale;
    }
    const format = {};
    format.currency = currencyCode || 'USD';
    if (hideCurrency === false) {
      format.style = 'currency';
      format.currencyDisplay = display;
    }
    if (noDecimalValues) {
      format.maximumFractionDigits = 0;
      format.minimumFractionDigits = 0;
    }

    try {
      formatter = new Intl.NumberFormat(
        selectedLocale || navigator.userLanguage || navigator.language,
        format
      );
    } catch {
      formatter = new Intl.NumberFormat(
        navigator.userLanguage || navigator.language || 'en-gb',
        {
          style: 'currency',
          currency: currencyCode || 'USD',
          currencyDisplay: display,
        }
      );
    }
    return formatter.format(amount);
  }

  return '';
};

export const assignAndCheckNewRoomRate = newRoomRate => {
  const paymentDetailProps = {};
  const newRoomRateData = get(newRoomRate, 'price', {});

  if (isEmpty(newRoomRateData)) {
    return;
  }

  if (!isEmpty(get(newRoomRateData, 'total_payable_amount'))) {
    paymentDetailProps.totalPayableAmount = get(
      newRoomRateData,
      'total_payable_amount',
      {}
    );
  }

  if (!isEmpty(get(newRoomRateData, 'total_exclusive_price'))) {
    paymentDetailProps.totalExclusivePrice = get(
      newRoomRateData,
      'total_exclusive_price',
      {}
    );
  }

  if (!isEmpty(get(newRoomRateData, 'total_inclusive_price'))) {
    paymentDetailProps.totalInclusivePrice = get(
      newRoomRateData,
      'total_inclusive_price',
      {}
    );
  }

  if (!isEmpty(get(newRoomRateData, 'discount_breakdown'))) {
    paymentDetailProps.discountBreakdown = get(
      newRoomRateData,
      'discount_breakdown',
      null
    );
  }

  if (!isEmpty(get(newRoomRateData, 'total_discounted_price'))) {
    paymentDetailProps.totalDiscountedPrice = get(
      newRoomRateData,
      'total_discounted_price',
      {}
    );
  }

  const taxBreakdown = get(newRoomRateData, 'tax_breakdown', []);
  const feeBreakdown = get(newRoomRateData, 'fee_breakdown', []);
  if (!isEmpty(taxBreakdown) || !isEmpty(feeBreakdown)) {
    paymentDetailProps.breakdownPrices = [...taxBreakdown, ...feeBreakdown];
  }

  return paymentDetailProps;
};

export const getQueryParam = (location, param) => {
  const params = new URLSearchParams(location);
  return params.get(param);
};
